import type {
    TraderSearchFilter,
    TraderSearchRequest,
} from '../../api/traderSearch';
import type { SearchFilterField } from '../api/searchTypes';
import { dirOf, readList, readParam } from './searchParams';

// URL parameters of the trader view → POST /api/trader-search (spec 4.7).
// Paging (limit, offset) and count_only are added by useTraderSearch.

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const LIST_KEYS = [
    'tags_include',
    'tags_exclude',
    'main_category_include',
    'main_category_exclude',
] as const;

type ListKey = (typeof LIST_KEYS)[number];

function isListKey(key: string): key is ListKey {
    return (LIST_KEYS as readonly string[]).includes(key);
}

export interface TraderQuery {
    address: string | null;
    hint: string | null;
}

export function traderQuery(q: string): TraderQuery {
    const text = q.trim();
    if (!text) return { address: null, hint: null };
    if (/^0x/i.test(text)) {
        const address = `0x${text.slice(2)}`;
        if (ADDRESS_RE.test(address)) return { address, hint: null };
        if (text.length === 42) {
            return {
                address: null,
                hint: 'That is not a valid address: 0x must be followed by 40 hex characters.',
            };
        }
        return {
            address: null,
            hint: 'Type the full 0x address (42 characters). Address prefixes are not searched.',
        };
    }
    if (text.startsWith('@')) {
        if (text.length > 1) return { address: text, hint: null };
        return {
            address: null,
            hint: 'Type the exact Polymarket name after the @.',
        };
    }
    return {
        address: null,
        hint: `The trader search takes a full 0x address or an exact @name, so “${text}” is ignored.`,
    };
}

// A full address or an @name looks up one trader, whatever the activity
// window says.
export function isAddressLookup(params: URLSearchParams): boolean {
    return traderQuery(params.get('q') ?? '').address !== null;
}

// Twelve significant digits drop the binary noise of the multiplication
// (57 × 0.01 is 0.5700000000000001 in floating point).
function scaled(value: number, scale: number | undefined): number {
    return scale === undefined
        ? value
        : Number((value * scale).toPrecision(12));
}

export function buildTraderRequest(
    params: URLSearchParams,
    spec: readonly SearchFilterField[],
    defaults: Record<string, string>,
    sortOptions: readonly string[]
): TraderSearchRequest {
    const request: TraderSearchRequest = { mode: 'advanced' };
    const filters: TraderSearchFilter[] = [];
    const lookup = isAddressLookup(params);

    for (const field of spec) {
        const key = field.key;

        if (key === 'q') {
            const { address } = traderQuery(params.get('q') ?? '');
            if (address) request.address = address;
            continue;
        }

        if (field.column && field.op) {
            const typed = params.get(key);
            // A number field with a default (the minimum volume) starts at it.
            // A typed 0 lifts it, and a lookup of one trader ignores it, as it
            // ignores the activity window; a minimum the user typed still holds.
            const hasDefault = defaults[key] !== undefined;
            if (hasDefault && typed === null && lookup) continue;
            const raw = (typed ?? defaults[key] ?? '').trim();
            const value = Number(raw);
            if (raw === '' || !Number.isFinite(value)) continue;
            if (hasDefault && value === 0) continue;
            filters.push({
                column: field.column,
                op: field.op,
                value: scaled(value, field.scale),
            });
            continue;
        }

        if (isListKey(key)) {
            const values = readList(params, key);
            if (values.length) request[key] = values;
            continue;
        }

        if (key === 'active_within_days') {
            const raw = readParam(params, key, defaults);
            const days = Number(raw);
            if (Number.isInteger(days) && days > 0)
                request.active_within_days = days;
            continue;
        }

        if (key === 'first_seen_after') {
            const value = params.get(key) ?? '';
            if (DATE_RE.test(value)) request.first_seen_after = value;
            continue;
        }

        if (key === 'tag_match') {
            request.tag_match =
                readParam(params, key, defaults) === 'all' ? 'all' : 'any';
        }
    }

    if (request.address) delete request.active_within_days;
    if (filters.length) request.filters = filters;

    const sort = readParam(params, 'sort', defaults);
    const sortBy = sortOptions.includes(sort) ? sort : defaults.sort;
    if (sortBy) request.sort_by = sortBy;
    request.sort_dir = dirOf(params, 'dir');
    return request;
}
